import { DateTimePicker, RadioGroup } from 'app/components/form-fields';
import clsx from 'clsx';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IAccessSharingPeriodType } from 'types/IAccessSharingUser';
import { getLocale } from 'locales/utils';
import moment from 'moment';
import './AccessPeriodRadioGroup.less';
import { useIsMobile } from 'app/hooks/useIsMobile';

interface Props {
  className?: string;
  value: IAccessSharingPeriodType;
  startDate: Date | null;
  endDate: Date | null;
  onChange: (value: IAccessSharingPeriodType) => void;
  onChangeStartDate: (value: Date | null) => void;
  onChangeEndDate: (value: Date | null) => void;
}

const DATE_FORMAT = 'MMMM dd, yyyy';
const FRENCH_TIME_FORMAT = 'HH:mm';
const DEFAULT_TIME_FORMAT = 'hh:mm a';

export default function AccessPeriodRadioGroup({
  className,
  value,
  startDate,
  endDate,
  onChange,
  onChangeStartDate,
  onChangeEndDate,
}: Props) {
  const { t } = useTranslation();

  const isMobile = useIsMobile();

  const locale = getLocale();
  const isFrenchLocale = locale.includes('fr');

  const options = useMemo(
    () => [
      {
        label: t('continous_access_period'),
        value: IAccessSharingPeriodType.Continuous,
      },
      {
        label: t('temporary_access_period'),
        value: IAccessSharingPeriodType.Temporary,
      },
    ],
    [t],
  );

  useEffect(() => {
    if (value === IAccessSharingPeriodType.Continuous) {
      onChangeStartDate(null);
      onChangeEndDate(null);
    }
  }, [value]);

  return (
    <div className={clsx('access-period-radio-group', className)}>
      <RadioGroup
        name="access_period"
        label={t('access_period')}
        value={value}
        onChange={value => {
          onChangeStartDate(null);
          onChangeEndDate(null);
          onChange(value);
        }}
        options={options}
      />

      {value === IAccessSharingPeriodType.Temporary && (
        <div className="access-period-radio-group__date-time-pickers">
          <DateTimePicker
            label={t('start_time')}
            value={startDate}
            clearable
            dateFormat={DATE_FORMAT}
            timeFormat={
              isFrenchLocale ? FRENCH_TIME_FORMAT : DEFAULT_TIME_FORMAT
            }
            datePlaceholder={t(
              isMobile ? 'select_start_time_mobile' : 'select_start_time',
            )}
            timePlaceholder={isFrenchLocale ? '00:00' : '12:00 AM'}
            timeDisabled={!startDate}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            min={moment().startOf('day').toDate()}
            max={
              !!endDate
                ? moment(endDate).subtract(1, 'day').toDate()
                : undefined
            }
            onChange={onChangeStartDate}
          />

          <DateTimePicker
            label={t('end_time')}
            value={endDate}
            clearable
            dateFormat={DATE_FORMAT}
            timeFormat={
              isFrenchLocale ? FRENCH_TIME_FORMAT : DEFAULT_TIME_FORMAT
            }
            datePlaceholder={t(
              isMobile ? 'select_end_time_mobile' : 'select_end_time',
            )}
            timeDisabled={!endDate}
            timePlaceholder={isFrenchLocale ? '00:00' : '12:00 AM'}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            min={moment(startDate ?? undefined)
              .add(1, 'days')
              .toDate()}
            onChange={onChangeEndDate}
          />
        </div>
      )}
    </div>
  );
}
